import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { ChartThemeProperty, IChartThemeModel } from '@nimbus-library/features/charts/models/chart-theme-property.enum';
import { DarkHandledRatioColors, DefaultHandledRatioColors } from '@nimbus-library/features/charts/models/handled-ratio.colors';
import { ChartTheme } from '../models/char-theme.enum';
import { ChartColors } from '../models/chart-colors';
import { ChartThemeActions } from './chart-theme.actions';

interface IChartThemeStateModel {
  theme: IChartThemeModel;
}
export const chartThemesMap: {
  [theme in (typeof ChartTheme)[keyof typeof ChartTheme]]: IChartThemeModel;
} = {
  Light: {
    line: ChartColors.Line,
    font: ChartColors.Font,
    empty: ChartColors.AshGreyPale,
    acceptanceSLA: ChartColors.AshGrey,
    allCalls: ChartColors.DarkGunmetal,
    allConversation: ChartColors.AllConversations,
    available: ChartColors.MountainMeadow,
    calendarOpen: ChartColors.CalendarOpen,
    calendarClosed: ChartColors.CalendarClosed,
    calendarHoliday: ChartColors.CalendarHoliday,
    calendarSpecial: ChartColors.CalendarSpecial,
    calendarSpecial2: ChartColors.CalendarSpecial2,
    calendarSpecial3: ChartColors.CalendarSpecial3,
    calendarSpecial4: ChartColors.CalendarSpecial4,
    chartSkillBased: ChartColors.HoneyFlower,
    connected: ChartColors.Ming,
    databarIndicator: ChartColors.DatabarIndicator,
    declined: ChartColors.BittersweetRed,
    enabled: ChartColors.JungleGreen,
    disabled: ChartColors.DimGrey,
    handled: ChartColors.MountainMeadow,
    handledNotHandledOverlay: ChartColors.FawnOrange,
    hangupBeforeAccept: ChartColors.FawnOrange,
    hangupInIvr: ChartColors.PaleCopper,
    hangupInQueue: ChartColors.BittersweetRed,
    hangupByWorkflow: ChartColors.RazzmicBerry,
    hangupSLA: ChartColors.HoneyFlower,
    inIvr: ChartColors.AshGrey,
    inQueue: ChartColors.HoneyFlower,
    inIvrAfterQueue: ChartColors.DarkGunmetal,
    legendOff: ChartColors.LegendOff,
    notAvailable: ChartColors.BittersweetRed,
    notHandled: ChartColors.BittersweetRed,
    queueTime: ChartColors.HoneyFlower,
    reachability: ChartColors.JungleGreen,
    outboundReachability: ChartColors.PaleCopper,
    rona: ChartColors.FawnOrange,
    summary: ChartColors.Summary,
    switchboardDisabled: ChartColors.SwitchboardDisabled,
    switchboardEnabled: ChartColors.SwitchboardEnabled,
    trendLine: ChartColors.HoneyFlower,
    heatmapRationColors: new DefaultHandledRatioColors(),
    active: ChartColors.JungleGreen,
    voicemail: ChartColors.VoiceMessage,
    TransferFailed: ChartColors.OldBrick,
    TransferredByWorkflow: ChartColors.Ming,
    TransferredByUser: ChartColors.SoftBlue,
    Transferring: ChartColors.JungleGreen,
    Parked: ChartColors.DustyOlive,
    PutOnHold: ChartColors.VividBlue,
    suspended: ChartColors.Suspended,
    uncompleted: ChartColors.Uncompleted,
    servicesRunning: ChartColors.ServicesRunning,
    pstnRunning: ChartColors.PSTNRunning,
    servicesApplied: ChartColors.ServicesApplied,
    pstnApplied: ChartColors.PSTNApplied,
    hangupAfterQueue: ChartColors.FawnOrange,
    hangupInIVRAfterQueue: ChartColors.DimGrey,
    other: ChartColors.HoneyFlower,
    destinationAccepted: ChartColors.MountainMeadowLight,
    destinationDeclined: ChartColors.FawnOrangeLight,
    destinationNotReached: ChartColors.FawnOrangeDark,
    lostInQueue: ChartColors.BittersweetRedDark,
    userAborted: ChartColors.PaleCopperLight,
    dialingOut: ChartColors.RazzmicBerry,
    licenseActive: ChartColors.JungleGreen,
    licenseTrial: ChartColors.RazzmicBerry,
    licenseSuspended: ChartColors.AshGrey,
    licenseExpired: ChartColors.DimGrey,
    licenseCanceled: ChartColors.BittersweetRed,
    licenseLab: ChartColors.Ming,
    canceledByExternalSystem: ChartColors.BittersweetRedLight,
    inbound: ChartColors.HoneyFlower,
    outbound: ChartColors.AshGrey,
    none: ChartColors.OldBrickDark,
    audio: ChartColors.HoneyFlower,
    instantMessaging: ChartColors.OldBrickDark,
    email: ChartColors.JungleGreen,
    externalTask: ChartColors.AshGreyDark,
    accepted: ChartColors.MountainMeadow,
    canceled: ChartColors.DimGrey,
    userSessionDestinationNotReached: ChartColors.PaleCopperDark,
    consulted: ChartColors.PaleCopper,
    transferred: ChartColors.VividBlue,
    outboundUserNotAccepted: ChartColors.DimGrey,
    outboundUserAborted: ChartColors.PaleCopperLight,
    markedAsHandledByUser: ChartColors.MountainMeadowDark,
    markedAsHandledByWorkflow: ChartColors.JungleGreenLight,
    ringing: ChartColors.FawnOrange,
    standbyDutyAccepted: ChartColors.MountainMeadow,
  },
  Dark: {
    line: ChartColors.LineDark,
    font: ChartColors.FontDark,
    empty: ChartColors.AshGreyDarkPale,
    acceptanceSLA: ChartColors.AshGreyDark,
    allCalls: ChartColors.DarkGunmetalDark,
    allConversation: ChartColors.AllConversations,
    available: ChartColors.MountainMeadowDark,
    calendarOpen: ChartColors.MountainMeadowDark,
    calendarClosed: ChartColors.BittersweetRedDark,
    calendarHoliday: ChartColors.SoftBlueDark,
    calendarSpecial: ChartColors.CalendarSpecial,
    calendarSpecial2: ChartColors.CalendarSpecial2,
    calendarSpecial3: ChartColors.CalendarSpecial3,
    calendarSpecial4: ChartColors.CalendarSpecial4,
    chartSkillBased: ChartColors.HoneyFlowerDark,
    connected: ChartColors.MingDark,
    databarIndicator: ChartColors.DatabarIndicator,
    declined: ChartColors.BittersweetRedDark,
    enabled: ChartColors.JungleGreenDark,
    disabled: ChartColors.DimGreyDark,
    handled: ChartColors.MountainMeadowDark,
    handledNotHandledOverlay: ChartColors.FawnOrangeDark,
    hangupBeforeAccept: ChartColors.FawnOrangeDark,
    hangupInIvr: ChartColors.PaleCopperDark,
    hangupInQueue: ChartColors.BittersweetRedDark,
    hangupByWorkflow: ChartColors.RazzmicBerryDark,
    hangupSLA: ChartColors.HoneyFlowerDark,
    inIvr: ChartColors.AshGreyDark,
    inQueue: ChartColors.HoneyFlowerDark,
    inIvrAfterQueue: ChartColors.DarkGunmetalDark,
    legendOff: ChartColors.LegendOff,
    notAvailable: ChartColors.BittersweetRedDark,
    notHandled: ChartColors.BittersweetRedDark,
    queueTime: ChartColors.HoneyFlowerDark,
    reachability: ChartColors.JungleGreenDark,
    outboundReachability: ChartColors.PaleCopperDark,
    rona: ChartColors.FawnOrangeDark,
    summary: ChartColors.Summary,
    switchboardDisabled: ChartColors.SwitchboardDisabled,
    switchboardEnabled: ChartColors.SwitchboardEnabled,
    trendLine: ChartColors.HoneyFlowerDark,
    heatmapRationColors: new DarkHandledRatioColors(),
    active: ChartColors.JungleGreenDark,
    voicemail: ChartColors.VoiceMessageDark,
    TransferFailed: ChartColors.OldBrick,
    TransferredByWorkflow: ChartColors.Ming,
    TransferredByUser: ChartColors.SoftBlue,
    Transferring: ChartColors.JungleGreenDark,
    Parked: ChartColors.DustyOliveDark,
    PutOnHold: ChartColors.VividBlueDark,
    suspended: ChartColors.Suspended,
    uncompleted: ChartColors.Uncompleted,
    servicesRunning: ChartColors.ServicesRunning,
    pstnRunning: ChartColors.PSTNRunning,
    servicesApplied: ChartColors.ServicesApplied,
    pstnApplied: ChartColors.PSTNApplied,
    hangupAfterQueue: ChartColors.FawnOrangeDark,
    hangupInIVRAfterQueue: ChartColors.DimGreyDark,
    other: ChartColors.HoneyFlowerDark,
    destinationAccepted: ChartColors.MountainMeadowLight,
    destinationDeclined: ChartColors.FawnOrangeLight,
    destinationNotReached: ChartColors.FawnOrangeDark,
    lostInQueue: ChartColors.BittersweetRedDark,
    userAborted: ChartColors.PaleCopperLight,
    dialingOut: ChartColors.RazzmicBerry,
    licenseActive: ChartColors.JungleGreenDark,
    licenseTrial: ChartColors.RazzmicBerry,
    licenseSuspended: ChartColors.AshGreyDark,
    licenseExpired: ChartColors.DimGreyDark,
    licenseCanceled: ChartColors.BittersweetRedDark,
    licenseLab: ChartColors.MingDark,
    canceledByExternalSystem: ChartColors.BittersweetRedLight,
    inbound: ChartColors.HoneyFlowerDark,
    outbound: ChartColors.AshGreyDark,
    none: ChartColors.OldBrickDark,
    audio: ChartColors.HoneyFlowerDark,
    instantMessaging: ChartColors.OldBrickDark,
    email: ChartColors.JungleGreen,
    externalTask: ChartColors.AshGreyDark,
    accepted: ChartColors.MountainMeadow,
    canceled: ChartColors.HoneyFlower,
    userSessionDestinationNotReached: ChartColors.PaleCopperDark,
    consulted: ChartColors.PaleCopperDark,
    transferred: ChartColors.VividBlueDark,
    outboundUserNotAccepted: ChartColors.DimGreyDark,
    outboundUserAborted: ChartColors.PaleCopperLight,
    markedAsHandledByUser: ChartColors.MountainMeadowDark,
    markedAsHandledByWorkflow: ChartColors.JungleGreenLight,
    ringing: ChartColors.FawnOrange,
    standbyDutyAccepted: ChartColors.MountainMeadow,
  },
  HighContrast: {
    line: ChartColors.Line,
    font: ChartColors.Font,
    empty: ChartColors.AshGrey,
    acceptanceSLA: ChartColors.AshGrey,
    allCalls: ChartColors.DarkGunmetal,
    allConversation: ChartColors.AllConversations,
    available: ChartColors.MountainMeadow,
    calendarOpen: ChartColors.MountainMeadow,
    calendarClosed: ChartColors.BittersweetRed,
    calendarHoliday: ChartColors.SoftBlue,
    calendarSpecial: ChartColors.CalendarSpecial,
    calendarSpecial2: ChartColors.CalendarSpecial2,
    calendarSpecial3: ChartColors.CalendarSpecial3,
    calendarSpecial4: ChartColors.CalendarSpecial4,
    chartSkillBased: ChartColors.HoneyFlowerDark,
    connected: ChartColors.Ming,
    databarIndicator: ChartColors.DatabarIndicator,
    declined: ChartColors.BittersweetRed,
    enabled: ChartColors.JungleGreen,
    disabled: ChartColors.DimGrey,
    handled: ChartColors.MountainMeadow,
    handledNotHandledOverlay: ChartColors.FawnOrange,
    hangupBeforeAccept: ChartColors.MountainMeadow,
    hangupInIvr: ChartColors.PaleCopper,
    hangupInQueue: ChartColors.BittersweetRed,
    hangupByWorkflow: ChartColors.RazzmicBerry,
    hangupSLA: ChartColors.HoneyFlower,
    inIvr: ChartColors.AshGrey,
    inQueue: ChartColors.HoneyFlower,
    inIvrAfterQueue: ChartColors.DarkGunmetal,
    legendOff: ChartColors.LegendOff,
    notAvailable: ChartColors.BittersweetRed,
    notHandled: ChartColors.BittersweetRed,
    queueTime: ChartColors.HoneyFlower,
    reachability: ChartColors.JungleGreen,
    outboundReachability: ChartColors.PaleCopper,
    rona: ChartColors.FawnOrange,
    summary: ChartColors.Summary,
    switchboardDisabled: ChartColors.SwitchboardDisabled,
    switchboardEnabled: ChartColors.SwitchboardEnabled,
    trendLine: ChartColors.HoneyFlower,
    heatmapRationColors: new DefaultHandledRatioColors(),
    active: ChartColors.JungleGreen,
    voicemail: ChartColors.VoiceMessageDark,
    TransferFailed: ChartColors.OldBrick,
    TransferredByWorkflow: ChartColors.Ming,
    TransferredByUser: ChartColors.SoftBlue,
    Transferring: ChartColors.JungleGreen,
    Parked: ChartColors.DustyOlive,
    PutOnHold: ChartColors.VividBlue,
    suspended: ChartColors.Suspended,
    uncompleted: ChartColors.Uncompleted,
    servicesRunning: ChartColors.ServicesRunning,
    pstnRunning: ChartColors.PSTNRunning,
    servicesApplied: ChartColors.ServicesApplied,
    pstnApplied: ChartColors.PSTNApplied,
    hangupAfterQueue: ChartColors.FawnOrange,
    hangupInIVRAfterQueue: ChartColors.DimGrey,
    other: ChartColors.HoneyFlower,
    destinationAccepted: ChartColors.MountainMeadowLight,
    destinationDeclined: ChartColors.FawnOrangeLight,
    destinationNotReached: ChartColors.FawnOrangeDark,
    lostInQueue: ChartColors.BittersweetRedDark,
    userAborted: ChartColors.PaleCopperLight,
    dialingOut: ChartColors.RazzmicBerry,
    licenseActive: ChartColors.JungleGreen,
    licenseTrial: ChartColors.RazzmicBerry,
    licenseSuspended: ChartColors.AshGrey,
    licenseExpired: ChartColors.DimGrey,
    licenseCanceled: ChartColors.BittersweetRed,
    licenseLab: ChartColors.Ming,
    canceledByExternalSystem: ChartColors.BittersweetRedLight,
    inbound: ChartColors.HoneyFlower,
    outbound: ChartColors.AshGrey,
    none: ChartColors.OldBrickDark,
    audio: ChartColors.HoneyFlower,
    instantMessaging: ChartColors.OldBrickDark,
    email: ChartColors.JungleGreen,
    externalTask: ChartColors.AshGreyDark,
    accepted: ChartColors.MountainMeadow,
    canceled: ChartColors.DimGrey,
    userSessionDestinationNotReached: ChartColors.PaleCopper,
    consulted: ChartColors.PaleCopper,
    transferred: ChartColors.VividBlue,
    outboundUserNotAccepted: ChartColors.DimGrey,
    outboundUserAborted: ChartColors.PaleCopperLight,
    markedAsHandledByUser: ChartColors.MountainMeadowDark,
    markedAsHandledByWorkflow: ChartColors.JungleGreenLight,
    ringing: ChartColors.FawnOrange,
    standbyDutyAccepted: ChartColors.MountainMeadow,
  },
};

@State<IChartThemeStateModel>({
  name: 'chartThemeState',
  defaults: {
    theme: chartThemesMap[ChartTheme.Default],
  },
})
@Injectable()
export class ChartThemeState {
  @Selector()
  public static theme(state: IChartThemeStateModel): IChartThemeModel {
    return state.theme;
  }

  @Selector()
  public static color(id: ChartThemeProperty) {
    return createSelector([ChartThemeState], (state: { chartThemeState: IChartThemeStateModel }) => state.chartThemeState.theme[id]);
  }

  @Action(ChartThemeActions.SetTheme)
  public setTheme({ patchState }: StateContext<IChartThemeStateModel>, { theme }: ChartThemeActions.SetTheme): void {
    patchState({ theme: chartThemesMap[theme] });
  }
}
